import React, { useState, useEffect } from 'react';
import { usePerformance } from '../contexts/PerformanceContext';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';

const PerformanceReviewForm = ({ review, onSubmit, onCancel, type = 'self' }) => {
  const { reviewTemplates, fetchReviewTemplates } = usePerformance();
  const { user } = useAuth();
  const [template, setTemplate] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Manager review specific fields
  const [overallRating, setOverallRating] = useState('meets_expectations');
  const [finalComments, setFinalComments] = useState('');
  const [developmentGoals, setDevelopmentGoals] = useState(['']);
  const [additionalComments, setAdditionalComments] = useState('');

  useEffect(() => {
    const loadTemplate = async () => {
      if (reviewTemplates.length === 0) {
        await fetchReviewTemplates();
      }
      
      // Find the template for this review
      const reviewTemplate = reviewTemplates.find(t => t.id === review.template_id);
      if (reviewTemplate) {
        setTemplate(reviewTemplate);
        
        // Initialize answers based on existing data or empty
        const existingAnswers = type === 'self' 
          ? review.self_assessment_answers || []
          : review.manager_review_answers || [];
          
        const initialAnswers = reviewTemplate.questions.map(question => {
          const existingAnswer = existingAnswers.find(a => a.question_id === question.id);
          return existingAnswer || {
            question_id: question.id,
            answer_text: '',
            rating_value: null,
            selected_options: []
          };
        });
        
        setAnswers(initialAnswers);
        
        // Load existing manager review data if editing
        if (type === 'manager' && review.overall_rating) {
          setOverallRating(review.overall_rating);
          setFinalComments(review.final_comments || '');
          setDevelopmentGoals(review.development_goals || ['']);
        }
      }
      setLoading(false);
    };

    loadTemplate();
  }, [review, reviewTemplates, type, fetchReviewTemplates]);

  const handleAnswerChange = (questionId, field, value) => {
    setAnswers(prev => prev.map(answer => 
      answer.question_id === questionId 
        ? { ...answer, [field]: value }
        : answer
    ));
  };

  const handleDevelopmentGoalChange = (index, value) => {
    const newGoals = [...developmentGoals];
    newGoals[index] = value;
    setDevelopmentGoals(newGoals);
  };

  const addDevelopmentGoal = () => {
    setDevelopmentGoals([...developmentGoals, '']);
  };

  const removeDevelopmentGoal = (index) => {
    setDevelopmentGoals(developmentGoals.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      if (type === 'self') {
        const result = await onSubmit({
          answers: answers,
          additional_comments: additionalComments
        });
        if (!result.success) {
          setError(result.error);
        }
      } else {
        const result = await onSubmit({
          answers: answers,
          overall_rating: overallRating,
          final_comments: finalComments,
          development_goals: developmentGoals.filter(goal => goal.trim() !== '')
        });
        if (!result.success) {
          setError(result.error);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (question) => {
    const answer = answers.find(a => a.question_id === question.id) || {};

    switch (question.question_type) {
      case 'rating_scale':
        return (
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              {[...Array(question.max_rating)].map((_, i) => (
                <label key={i + 1} className="flex items-center">
                  <input
                    type="radio"
                    name={`rating_${question.id}`}
                    value={i + 1}
                    checked={answer.rating_value === i + 1}
                    onChange={(e) => handleAnswerChange(question.id, 'rating_value', parseInt(e.target.value))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-1 text-sm text-gray-700">{i + 1}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>Poor</span>
              <span>Excellent</span>
            </div>
          </div>
        );

      case 'text_area':
        return (
          <textarea
            rows={4}
            value={answer.answer_text || ''}
            onChange={(e) => handleAnswerChange(question.id, 'answer_text', e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder="Enter your response..."
          />
        );

      case 'multiple_choice':
        return (
          <div className="space-y-2">
            {question.options.map((option, index) => (
              <label key={index} className="flex items-center">
                <input
                  type="checkbox"
                  checked={answer.selected_options?.includes(option) || false}
                  onChange={(e) => {
                    const currentOptions = answer.selected_options || [];
                    const newOptions = e.target.checked
                      ? [...currentOptions, option]
                      : currentOptions.filter(o => o !== option);
                    handleAnswerChange(question.id, 'selected_options', newOptions);
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">{option}</span>
              </label>
            ))}
          </div>
        );

      case 'yes_no':
        return (
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name={`yesno_${question.id}`}
                value="yes"
                checked={answer.answer_text === 'yes'}
                onChange={(e) => handleAnswerChange(question.id, 'answer_text', e.target.value)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Yes</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name={`yesno_${question.id}`}
                value="no"
                checked={answer.answer_text === 'no'}
                onChange={(e) => handleAnswerChange(question.id, 'answer_text', e.target.value)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">No</span>
            </label>
          </div>
        );

      case 'goal_assessment':
        return (
          <div className="space-y-4">
            <textarea
              rows={3}
              value={answer.answer_text || ''}
              onChange={(e) => handleAnswerChange(question.id, 'answer_text', e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Assess goal achievement and provide details..."
            />
            {review.linked_goals && review.linked_goals.length > 0 && (
              <div className="bg-gray-50 p-3 rounded-md">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Related Goals:</h4>
                <ul className="space-y-1">
                  {review.linked_goals.slice(0, 3).map((goal, index) => (
                    <li key={index} className="text-xs text-gray-600">
                      • {goal.title} ({Math.round(goal.progress_percentage)}% complete)
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={answer.answer_text || ''}
            onChange={(e) => handleAnswerChange(question.id, 'answer_text', e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder="Enter your response..."
          />
        );
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" text="Loading review form..." />;
  }

  if (!template) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Template not found</h3>
        <p className="text-gray-500">The review template could not be loaded.</p>
      </div>
    );
  }

  const isManager = type === 'manager';
  const formTitle = isManager ? 'Manager Review' : 'Self-Assessment';

  return (
    <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900">{formTitle}</h2>
        <p className="text-gray-600 mt-2">{template.name}</p>
        {template.description && (
          <p className="text-sm text-gray-500 mt-1">{template.description}</p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-8">
        {/* Review Questions */}
        {template.questions.map((question, index) => (
          <div key={question.id} className="space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900">
                  {index + 1}. {question.question_text}
                  {question.is_required && <span className="text-red-500 ml-1">*</span>}
                </h3>
                {question.weight !== 1.0 && (
                  <p className="text-sm text-gray-500">Weight: {question.weight}x</p>
                )}
              </div>
            </div>
            
            <div className="ml-4">
              {renderQuestion(question)}
            </div>
          </div>
        ))}

        {/* Additional Comments for Self-Assessment */}
        {!isManager && (
          <div className="space-y-3">
            <h3 className="text-lg font-medium text-gray-900">Additional Comments</h3>
            <textarea
              rows={4}
              value={additionalComments}
              onChange={(e) => setAdditionalComments(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Any additional comments or feedback..."
            />
          </div>
        )}

        {/* Manager Review Specific Fields */}
        {isManager && (
          <>
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-gray-900">Overall Performance Rating *</h3>
              <select
                value={overallRating}
                onChange={(e) => setOverallRating(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="exceptional">Exceptional (5)</option>
                <option value="exceeds_expectations">Exceeds Expectations (4)</option>
                <option value="meets_expectations">Meets Expectations (3)</option>
                <option value="below_expectations">Below Expectations (2)</option>
                <option value="unsatisfactory">Unsatisfactory (1)</option>
              </select>
            </div>

            <div className="space-y-3">
              <h3 className="text-lg font-medium text-gray-900">Development Goals</h3>
              {developmentGoals.map((goal, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={goal}
                    onChange={(e) => handleDevelopmentGoalChange(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter development goal..."
                  />
                  {developmentGoals.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeDevelopmentGoal(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addDevelopmentGoal}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                + Add Development Goal
              </button>
            </div>

            <div className="space-y-3">
              <h3 className="text-lg font-medium text-gray-900">Final Comments</h3>
              <textarea
                rows={4}
                value={finalComments}
                onChange={(e) => setFinalComments(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Overall feedback and recommendations..."
              />
            </div>
          </>
        )}

        {/* Error Message */}
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {error}
                </h3>
              </div>
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center"
          >
            {submitting ? (
              <LoadingSpinner size="sm" text="" />
            ) : (
              `Submit ${formTitle}`
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PerformanceReviewForm;